import { useQueries, useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@/utils/auth/auth0'
import * as API from '../../api/scansAPI'

interface ScanIdsData {
  fundingNeedId: number
  scanIds: any[]
}

interface UseScanIdsByNeedIdResponse {
  data: ScanIdsData[]
  isLoading: boolean
  isError: boolean
}

export function useScanIdsByNeedId(token: string, fundingNeedIds: number[]) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQueries({
    queries: fundingNeedIds.map((fundingNeedId) => ({
      queryKey: ['scanIds', fundingNeedId],
      queryFn: async () => {
        const freshToken = await getAccessTokenSilently()
        const scanIds = await API.fetchScanIdsByNeedId(freshToken, fundingNeedId)
        return { fundingNeedId, scanIds }
      },
      enabled: !!token && !!fundingNeedId,
      retry: 3,
    })),
  })

  const data = query.map((result) => result.data)
  const isLoading = query.some((result) => result.isLoading)
  const isError = query.some((result) => result.error)

  return { data, isLoading, isError }
}

export function useScanResults(token: string, scanIds: number[]) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['scanResults', scanIds],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchScanResultsByScanIds(freshToken, scanIds)
    },
    enabled: !!token && !!scanIds.length,
    retry: 3,
  })

  return {
    data: Array.isArray(query.data) ? query.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// Slow enough to be cheap on a page that mostly sits idle, quick enough that a
// scan started elsewhere shows up while the user is still looking at the page.
// Only worth paying on a page watching a single need — see `poll` below.
const ACTIVE_SCAN_POLL_MS = 15000

/**
 * Whether a scan is running for this need, according to the server.
 *
 * Results are written in one batch near the end of a scan, so an empty results
 * list means either "no scan has ever run" or "the first one is still going" —
 * only the server can tell those apart for a scan this browser didn't start.
 *
 * Callers disable this once they are tracking the scan themselves; progress then
 * comes from the job's event stream rather than from polling.
 *
 * `poll` is the repeat interval, not the query. It is off by default for callers
 * rendering a list: the interval runs precisely while no scan is known, so N rows
 * on an idle org would poll N times every 15s indefinitely, and each request costs
 * two DB round trips before the redis lookup. The fetch on mount is what answers
 * "was a scan already running when this page loaded", and react-query still
 * refetches on window focus, so a scan started elsewhere is picked up on return to
 * the tab rather than never.
 */
export function useActiveScanForNeed(
  token: string,
  fundingNeedId: number,
  enabled: boolean = true,
  poll: boolean = false
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['activeScanForNeed', fundingNeedId],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchActiveScanForNeed(freshToken, fundingNeedId)
    },
    enabled: !!token && !!fundingNeedId && enabled,
    refetchInterval: enabled && poll ? ACTIVE_SCAN_POLL_MS : false,
    retry: 3,
  })

  return {
    activeScan: query.data ?? null,
    isFetched: query.isFetched,
    isError: query.isError,
  }
}

export function useOrgScanUsage(token: string, orgUuid: string | undefined) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['orgScanUsage', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchOrgScanUsage(freshToken, orgUuid as string)
    },
    enabled: !!token && !!orgUuid,
    retry: 3,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

export function useLatestScanResultsByNeedId(
  token: string,
  fundingNeedId: number,
  showHiddenMatches: boolean = false
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['scanResultsByNeedId', fundingNeedId, showHiddenMatches],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchLatestScanResultsByNeedId(
        freshToken,
        fundingNeedId,
        showHiddenMatches
      )
    },
    enabled: !!token && !!fundingNeedId,
    retry: 3,
  })

  return {
    data: Array.isArray(query.data) ? query.data : undefined,
    isLoading: query.isLoading,
    // Callers that share this cache key with a list view need isFetching too:
    // isLoading is false once anything is cached, so a stale empty array reads
    // as a settled "no results" while the real fetch is still in flight.
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  }
}
